import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./Transactions.css";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    transactionType: "",
  });
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [editingNotes, setEditingNotes] = useState({});
  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const getAction = (quantity) => {
    if (quantity === undefined || quantity === null) return "N/A";
    return quantity > 0 ? "Buy" : "Sell";
  };

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedTransactions = [...transactions].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setTransactions(sortedTransactions);
  };

  const handleManualFetch = async () => {
    try {
      setError("Starting transaction fetch..."); // Give user feedback
      const response = await axios.get(`${API_BASE_URL}/fetch-transactions-now`);
      setError(`Fetch complete: ${response.data.message}`);
      // Optionally, you could re-fetch the transaction list here
      // await fetchTransactions(); // You'd need to convert fetchTransactions to be callable
    } catch (err) {
      console.error("Error triggering manual fetch:", err);
      const errorMessage =
        err.response?.data?.detail || err.message || "Failed to trigger fetch";
      setError(`Fetch error: ${errorMessage}`);
    }
  };

  const exportToExcel = () => {
    const exportData = transactions.map((tx) => ({
      Date: formatDate(tx.transaction_date),
      Type: tx.transaction_type || "N/A",
      Description: tx.description || "N/A",
      Symbol: tx.symbol || "N/A",
      ID: tx.cusip || "N/A",
      Action: getAction(tx.quantity),
      Quantity: tx.quantity !== undefined ? tx.quantity : 0,
      Price: tx.price !== undefined ? tx.price : 0,
      Amount: tx.amount !== undefined ? tx.amount : 0,
      Fees: tx.fees !== undefined ? tx.fees : 0,
      Notes: tx.notes || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "transactions.xlsx");
  };

  const saveNotes = async (transactionId, notes) => {
    try {
      await axios.post(`${API_BASE_URL}/update-transaction-notes`, {
        transaction_id: transactionId,
        notes,
      });
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.transaction_id === transactionId ? { ...tx, notes } : tx
        )
      );
      setEditingNotes((prev) => ({ ...prev, [transactionId]: false }));
      setError("");
    } catch (err) {
      console.error("Error saving notes:", err);
      const errorMessage =
        err.response?.data?.detail || err.message || "Failed to save notes";
      setError(`Failed to save notes: ${errorMessage}`);
    }
  };

  const handleNotesChange = (transactionId, value) => {
    setEditingNotes((prev) => ({
      ...prev,
      [transactionId]: { ...prev[transactionId], notes: value },
    }));
  };

  const toggleEditNotes = (transactionId, currentNotes) => {
    setEditingNotes((prev) => ({
      ...prev,
      [transactionId]: {
        editing: !prev[transactionId]?.editing,
        notes: prev[transactionId]?.notes ?? currentNotes ?? "",
      },
    }));
  };

  useEffect(() => {
    const fetchTransactionTypes = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get-transaction-types`);
        console.log("Transaction Types Response:", response.data);
        setTransactionTypes(response.data.transaction_types || []);
      } catch (err) {
        setError("Failed to fetch transaction types");
        console.error("Error fetching transaction types:", err);
      }
    };
    fetchTransactionTypes();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const params = {};
        if (filters.startDate) params.start_date = filters.startDate;
        if (filters.endDate) params.end_date = filters.endDate;
        if (filters.transactionType)
          params.transaction_type = filters.transactionType;

        console.log("Fetching transactions with params:", params);
        const response = await axios.get(`${API_BASE_URL}/get-transactions`, {
          params,
        });
        console.log("Transactions API Response (Raw):", response.data);
        const fetchedTransactions = response.data.transactions || [];
        console.log("Parsed Transactions:", fetchedTransactions);
        setTransactions(fetchedTransactions);
        setError("");
      } catch (err) {
        setError("Failed to fetch transactions");
        console.error("Error fetching transactions:", err);
        setTransactions([]);
      }
    };
    fetchTransactions();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      transactionType: "",
    });
  };

  return (
    <div className="transactions-container">
      <h2>Transactions</h2>
      <div className="filters">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
          />
        </div>
        <div className="filter-group">
          <label>Transaction Type:</label>
          <select
            name="transactionType"
            value={filters.transactionType}
            onChange={handleFilterChange}
          >
            <option value="">All Types</option>
            {transactionTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <button onClick={resetFilters}>Reset Filters</button>
        <button onClick={exportToExcel} className="download-excel">
          Download as Excel
        </button>

        <button onClick={handleManualFetch} style={{ backgroundColor: "#ffc107" }}>
          Manually Fetch Transactions
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      <table className="transactions-table">
        <thead>
          <tr>
            <th
              onClick={() => sortData("transaction_date")}
              style={{ cursor: "pointer" }}
            >
              Date{" "}
              {sortConfig.key === "transaction_date"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("transaction_type")}
              style={{ cursor: "pointer" }}
            >
              Type{" "}
              {sortConfig.key === "transaction_type"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("description")}
              style={{ cursor: "pointer" }}
            >
              Description{" "}
              {sortConfig.key === "description"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("symbol")}
              style={{ cursor: "pointer" }}
            >
              Symbol{" "}
              {sortConfig.key === "symbol"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("cusip")}
              style={{ cursor: "pointer" }}
            >
              ID{" "}
              {sortConfig.key === "cusip"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th>Action</th>
            <th
              onClick={() => sortData("quantity")}
              style={{ cursor: "pointer" }}
            >
              Quantity{" "}
              {sortConfig.key === "quantity"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("price")}
              style={{ cursor: "pointer" }}
            >
              Price{" "}
              {sortConfig.key === "price"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th
              onClick={() => sortData("amount")}
              style={{ cursor: "pointer" }}
            >
              Amount{" "}
              {sortConfig.key === "amount"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th onClick={() => sortData("fees")} style={{ cursor: "pointer" }}>
              Fees{" "}
              {sortConfig.key === "fees"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : ""}
            </th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length > 0 ? (
            transactions.map((tx, index) => (
              <tr key={tx.transaction_id || index}>
                <td>{formatDate(tx.transaction_date)}</td>
                <td>{tx.transaction_type || "N/A"}</td>
                <td>{tx.description || "N/A"}</td>
                <td>{tx.symbol || "N/A"}</td>
                <td>{tx.cusip || "N/A"}</td>
                <td>{getAction(tx.quantity)}</td>
                <td>{tx.quantity !== undefined ? tx.quantity : 0}</td>
                <td>${(tx.price !== undefined ? tx.price : 0).toFixed(2)}</td>
                <td>${(tx.amount !== undefined ? tx.amount : 0).toFixed(2)}</td>
                <td>${(tx.fees !== undefined ? tx.fees : 0).toFixed(2)}</td>
                <td>
                  {editingNotes[tx.transaction_id]?.editing ? (
                    <input
                      type="text"
                      className="notes-input"
                      value={editingNotes[tx.transaction_id]?.notes || ""}
                      onChange={(e) =>
                        handleNotesChange(tx.transaction_id, e.target.value)
                      }
                    />
                  ) : (
                    tx.notes || ""
                  )}
                </td>
                <td>
                  {editingNotes[tx.transaction_id]?.editing ? (
                    <>
                      <button
                        className="action-button"
                        onClick={() =>
                          saveNotes(
                            tx.transaction_id,
                            editingNotes[tx.transaction_id].notes
                          )
                        }
                      >
                        Save
                      </button>
                      <button
                        className="action-button cancel"
                        onClick={() =>
                          toggleEditNotes(tx.transaction_id, tx.notes)
                        }
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="action-button"
                      onClick={() =>
                        toggleEditNotes(tx.transaction_id, tx.notes)
                      }
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="12">No transactions found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Transactions;
